import { Button, Col, DatePicker, Form, Input, Row, Select } from "antd";
import { useForm } from "antd/lib/form/Form";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { contactsStore } from "Stores/ContactsStore";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { BcfIssueViewpoints } from "./BcfIssueViewpoints";
import { ViewpointManager } from "./ViewpointManager";
import { dayjs } from "Config/dayjs";
import { BcfServerTopicWithModelIdAndDeleted, BCFStore } from "Components/XKTViewer/Stores/BCFStore";
import { API } from "General/API";
import { projectsDataStore } from "Stores";
import { API_URL } from "General/APIEndpoints";
import { useAssignedToOptions } from "./hooks/useAssignedToOptions";

interface BcfEditIssueFormProps {
  topic: BcfServerTopicWithModelIdAndDeleted;
  bcfStore: BCFStore;
  viewpointManager: ViewpointManager;
  hideModal: () => void;
  setConfirmOnCancel?: (b: boolean) => void;
  setOnSave?: (fn: () => void) => void;
  onEnterAddViewpointMode: () => void;
  isTopicDisabled?: boolean;
}

export const BcfEditIssueForm = observer(
  ({
    topic,
    bcfStore,
    hideModal,
    setConfirmOnCancel,
    setOnSave,
    onEnterAddViewpointMode,
    viewpointManager,
    isTopicDisabled,
  }: BcfEditIssueFormProps) => {
    const [form] = useForm();
    const { t } = useTranslation(I18N_NS.PROJECT);
    const [loading, setLoading] = React.useState(false);
    const [comments, setComments] = React.useState<API_BcfServerComment[]>([]);
    const isDirtyRef = React.useRef(false);

    React.useEffect(() => {
      setOnSave?.(() => form.submit());
    }, [form, setOnSave]);

    const handleValuesChange = React.useCallback(() => {
      if (!isDirtyRef.current) {
        isDirtyRef.current = true;
        setConfirmOnCancel?.(true);
      }
    }, [setConfirmOnCancel]);

    const handleFinish = async (values) => {
      try {
        setLoading(true);
        await viewpointManager.save();
        await bcfStore.updateTopic(topic.guid, values);
        notificationSuccess(t("Success"), t("Issue edited", { ns: I18N_NS.XKT_VIEWER }));
        setConfirmOnCancel?.(false);
        hideModal();
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
      }
    };

    const saveInputData = () => {
      const values = form.getFieldsValue();
      const payload = JSON.stringify({ ...topic, ...values });
      bcfStore.storeFormData(payload);
    };

    const enableViewpointAddMode = () => {
      saveInputData();
      onEnterAddViewpointMode();
    };

    const hasUpdateRights = React.useMemo(
      () => (topic.authorization.topic_actions as string[]).includes("update"),
      [topic.guid]
    );

    const fetchComments = React.useCallback(async () => {
      if (!topic?.guid) return;

      try {
        const response = await API.getData<API_BcfServerComment[]>(
          API_URL.BCF_SERVER_COMMENTS.bcfServerGetComments(projectsDataStore.bcfGuid, topic.guid),
          { $orderby: "date asc", includeAuthorization: true }
        );
        setComments(response);
      } catch (error) {
        console.error("Failed to fetch comments:", error);
      }
    }, [topic?.guid]);

    React.useEffect(() => {
      if (topic) {
        const formData = { ...topic };
        if (formData.due_date) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          formData.due_date = dayjs(formData.due_date) as any;
        }
        form.setFieldsValue(formData);
        fetchComments();
      }
    }, []);

    const assignedToOptions = useAssignedToOptions(bcfStore.projectUsers, topic.assigned_to_id, topic.assigned_to);

    return (
      <Form
        form={form}
        id="bcf_edit_form"
        layout="vertical"
        onFinish={handleFinish}
        onValuesChange={handleValuesChange}
        disabled={isTopicDisabled || !bcfStore.extensions.topic_actions?.includes("update")}
      >
        <Row gutter={{ sm: 10, md: 20 }}>
          <Col sm={12} md={16} lg={18}>
            <Form.Item
              name="title"
              label={t("Title")}
              rules={[{ required: true, message: t("This field is required", { ns: I18N_NS.VALIDATION }) }]}
            >
              {hasUpdateRights ? <Input autoFocus placeholder={t("Title")} /> : topic.title}
            </Form.Item>
          </Col>
          <Col sm={12} md={8} lg={6}>
            <Form.Item name="topic_status" label={t("Status")}>
              {hasUpdateRights && topic.authorization?.topic_status?.length ? (
                <Select<string>
                  placeholder={t("Status")}
                  options={topic.authorization?.topic_status.map((item) => ({ label: item, value: item }))}
                  getPopupContainer={() => document.getElementById("bcf_edit_form")}
                />
              ) : (
                topic.topic_status
              )}
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={20}>
          <Col md={8} lg={6}>
            <Form.Item name="topic_type" label={t("Type")}>
              {hasUpdateRights ? (
                <Select<string>
                  placeholder={t("Type")}
                  options={bcfStore.extensions.topic_type?.map((item) => ({ label: item, value: item }))}
                  getPopupContainer={() => document.getElementById("bcf_edit_form")}
                />
              ) : (
                topic.topic_type
              )}
            </Form.Item>
          </Col>
          <Col md={8} lg={6}>
            <Form.Item name="assigned_to_id" label={t("Assigned to")}>
              {hasUpdateRights ? (
                <Select
                  placeholder={t("Assigned to")}
                  showSearch={{ filterOption: true }}
                  options={assignedToOptions}
                  getPopupContainer={() => document.getElementById("bcf_edit_form")}
                />
              ) : (
                contactsStore.getUserNameById(topic.assigned_to_id)
              )}
            </Form.Item>
          </Col>
          <Col sm={12} md={8} lg={6}>
            <Form.Item name="due_date" label={t("Due date")}>
              {hasUpdateRights ? (
                <DatePicker
                  placeholder={t("Due date")}
                  format="L"
                  style={{ width: "100%" }}
                  getPopupContainer={() => document.getElementById("bcf_edit_form")}
                />
              ) : (
                <>{topic.due_date ? dayjs(topic.due_date).format("L") : null}</>
              )}
            </Form.Item>
          </Col>
          <Col md={8} lg={6}>
            <Form.Item name="priority" label={t("Priority")}>
              {hasUpdateRights ? (
                <Select<string>
                  placeholder={t("Priority")}
                  options={bcfStore.extensions.priority.map((item) => ({ label: item, value: item }))}
                  getPopupContainer={() => document.getElementById("bcf_edit_form")}
                />
              ) : (
                topic.priority
              )}
            </Form.Item>
          </Col>
          <Col md={8} lg={6}>
            <Form.Item name="stage" label={t("Stage")}>
              {hasUpdateRights ? (
                <Select<string>
                  placeholder={t("Stage")}
                  options={bcfStore.stageOptions}
                  getPopupContainer={() => document.getElementById("bcf_edit_form")}
                  allowClear
                />
              ) : (
                topic.stage
              )}
            </Form.Item>
          </Col>
          <Col md={8} lg={6}>
            <Form.Item name="labels" label={t("Label")}>
              {hasUpdateRights ? (
                <Select<string[]>
                  mode="multiple"
                  placeholder={t("Label")}
                  options={bcfStore.labelOptions}
                  getPopupContainer={() => document.getElementById("bcf_edit_form")}
                />
              ) : (
                topic.labels?.join(", ")
              )}
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="description" label={t("Description")}>
          {hasUpdateRights ? (
            <Input.TextArea autoSize={{ minRows: 8 }} placeholder={t("Description")} />
          ) : (
            <span style={{ whiteSpace: "pre" }}>{topic.description}</span>
          )}
        </Form.Item>
        <Form.Item label={t("Viewpoints")}>
          <BcfIssueViewpoints
            viewpointManager={viewpointManager}
            onEnableViewpointAddMode={enableViewpointAddMode}
            addDelete={!isTopicDisabled}
            comments={comments}
            setConfirmOnCancel={setConfirmOnCancel}
          />
        </Form.Item>
        <ButtonsRow justify="space-between">
          <Button disabled={false} onClick={hideModal}>
            {t("Cancel")}
          </Button>
          {bcfStore.extensions.topic_actions?.includes("update") && (
            <Button type="primary" htmlType="submit" loading={loading}>
              {t("Submit")}
            </Button>
          )}
        </ButtonsRow>
      </Form>
    );
  }
);
